import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Sparkles, ClipboardList } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { getConciergeDisplayName } from "@/lib/i18n";
import { storePostLoginRedirect } from "@/lib/auth-redirect";
import { StoreEntryCard } from "@/components/StoreEntryCard";
import {
  createConciergeRequest,
  listMyConciergeRequests,
} from "@/lib/concierge.functions";
import {
  deriveRequestState,
  getRequestStateLabel,
  type RequestState,
} from "@/lib/concierge-workflow";

export const Route = createFileRoute("/concierge/")({ component: ConciergePage });

const STATUS_STYLE: Record<string, string> = {
  novo: "bg-blue-100 text-blue-800",
  em_analise: "bg-amber-100 text-amber-800",
  em_desenvolvimento: "bg-purple-100 text-purple-800",
  concluido: "bg-emerald-100 text-emerald-800",
  entregue: "bg-emerald-200 text-emerald-900",
  cancelado: "bg-neutral-200 text-neutral-700",
};

function ConciergePage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { lang } = useLang();
  const conciergeName = getConciergeDisplayName(lang);
  const navigate = useNavigate();
  const createFn = useServerFn(createConciergeRequest);
  const listFn = useServerFn(listMyConciergeRequests);

  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState<Array<{
    id: string;
    destination: string;
    start_date: string | null;
    end_date: string | null;
    status: string;
    created_at: string;
    payment_status?: string | null;
    delivered_at?: string | null;
    is_first_free?: boolean | null;
    price_cents?: number | null;
    app_route_link?: string | null;
  }>>([]);

  // form
  const [destination, setDestination] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [people, setPeople] = useState(2);
  const [budget, setBudget] = useState<string>("");
  const [interests, setInterests] = useState("");
  const [transport, setTransport] = useState("");
  const [accommodation, setAccommodation] = useState("");
  const [notes, setNotes] = useState("");

  // A apresentação é pública: só o envio do pedido pede sessão iniciada.

  useEffect(() => {
    if (!isAuthenticated) return;
    listFn().then((r) => setRequests(r as typeof requests)).catch(() => {});
  }, [isAuthenticated, listFn]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    if (!isAuthenticated) {
      storePostLoginRedirect();
      navigate({ to: "/login" });
      return;
    }
    if (destination.trim().length < 2) {
      toast.error("Indica o destino.");
      return;
    }
    setSubmitting(true);
    try {
      await createFn({
        data: {
          destination: destination.trim(),
          start_date: startDate || null,
          end_date: endDate || null,
          people: Math.max(1, Number(people) || 1),
          budget: budget ? Number(budget) : null,
          currency: "EUR",
          interests: interests
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
            .slice(0, 30),
          transport: transport || null,
          accommodation: accommodation || null,
          notes: notes || null,
          attachments: [],
          lang,
        },
      });
      toast.success("Pedido enviado! A equipa Routify vai analisar.");
      setDestination(""); setStartDate(""); setEndDate(""); setPeople(2);
      setBudget(""); setInterests(""); setTransport(""); setAccommodation(""); setNotes("");
      const r = await listFn();
      setRequests(r as typeof requests);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao enviar");
    } finally {
      setSubmitting(false);
    }
  }

  if (authLoading) {
    return (
      <main className="mx-auto flex min-h-[60vh] w-full max-w-3xl items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-8">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Início
      </Link>

      <header className="mt-4 mb-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">
          <Sparkles className="h-3.5 w-3.5" /> Novo serviço
        </div>
        <h1 className="mt-2 text-3xl font-bold">{conciergeName}</h1>
        <p className="mt-1 text-muted-foreground">
          Preenche o formulário e a equipa Routify prepara um roteiro à medida.
        </p>
      </header>

      <div className="mb-6">
        <StoreEntryCard />
      </div>



      <form onSubmit={submit} className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-sm">
        <Field label="Destino *">
          <input required value={destination} onChange={(e) => setDestination(e.target.value)}
            className="input" placeholder="Ex.: Roma e Florença" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Data de início">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="input" />
          </Field>
          <Field label="Data de fim">
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="input" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Nº de pessoas">
            <input type="number" min={1} max={50} value={people} onChange={(e) => setPeople(Number(e.target.value))} className="input" />
          </Field>
          <Field label="Orçamento total (€)">
            <input type="number" min={0} value={budget} onChange={(e) => setBudget(e.target.value)} className="input" placeholder="Opcional" />
          </Field>
        </div>

        <Field label="Interesses (separa por vírgulas)">
          <input value={interests} onChange={(e) => setInterests(e.target.value)}
            className="input" placeholder="História, gastronomia, natureza" />
        </Field>

        <Field label="Meio de transporte preferido">
          <select value={transport} onChange={(e) => setTransport(e.target.value)} className="input">
            <option value="">— Escolher —</option>
            <option value="walk">A pé</option>
            <option value="bike">Bicicleta</option>
            <option value="transit">Transportes públicos</option>
            <option value="car">Carro</option>
            <option value="mixed">Misto</option>
          </select>
        </Field>

        <Field label="Alojamento (opcional)">
          <input value={accommodation} onChange={(e) => setAccommodation(e.target.value)}
            className="input" placeholder="Hotel, Airbnb, morada..." />
        </Field>

        <Field label="Observações">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            className="input min-h-[100px]" placeholder="Restrições alimentares, ritmo, tudo o que quiseres partilhar" />
        </Field>

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand/90 disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Enviar pedido
        </button>

        <p className="text-center text-xs text-muted-foreground">
          Atenção: este pedido envolve custos adicionais. A equipa Routify vai analisar o pedido e enviar-te uma proposta de valor por email — normalmente até 24h. Só pagas depois de aceitares a proposta.
        </p>
      </form>

      {requests.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold">
            <ClipboardList className="h-5 w-5" /> Os meus pedidos
          </h2>
          <ul className="space-y-2">
            {requests.map((r) => {
              const displayStatus = deriveRequestState({
                status: r.status,
                paymentStatus: r.payment_status ?? "",
                isFirstFree: Boolean(r.is_first_free),
                priceCents: r.price_cents ?? null,
                deliveredAt: r.delivered_at ?? null,
                hasDeliverable: Boolean(r.app_route_link),
                activeQuote: null,
              });
              const STYLE: Record<string, string> = {
                ...STATUS_STYLE,
                orcamento_enviado: "bg-amber-100 text-amber-900",
                aguarda_pagamento: "bg-amber-100 text-amber-900",
                pago: "bg-emerald-100 text-emerald-800",
                pronto: "bg-sky-100 text-sky-800",
                entregue: "bg-emerald-100 text-emerald-800",
              };
              return (
              <li key={r.id}>
                <Link to="/concierge/$id" params={{ id: r.id }}
                  className="flex flex-col items-center rounded-xl border border-border bg-card p-4 text-center hover:bg-accent/50 transition">
                  <div>
                    <div className="font-medium">{r.destination}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.start_date || "—"} → {r.end_date || "—"} · {new Date(r.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <span className={`mt-2 rounded-full px-2 py-0.5 text-[10px] font-semibold leading-tight ${STYLE[displayStatus] ?? "bg-neutral-200"}`}>
                    {getRequestStateLabel(displayStatus as RequestState, lang)}
                  </span>
                </Link>
              </li>
              );
            })}
          </ul>
        </section>
      )}

      <style>{`
        .input {
          width: 100%; border-radius: 10px; border: 1px solid var(--border);
          background: var(--background); color: var(--foreground); padding: 8px 12px; font-size: 14px;
        }
        .input:focus { outline: 2px solid var(--ring); outline-offset: 2px; }
      `}</style>
    </main>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-foreground">{label}</span>
      {children}
    </label>
  );
}
